import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import Underline from '@tiptap/extension-underline'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import type { BlockStyle } from './BlockStyleDropdown'
import {
  normalizeRichEditorHtmlForStorage,
  plainTextToRichEditorHtml,
  prepareRichEditorContent
} from './normalizeRichEditorHtmlForStorage'
import { RichEditorFooterActions } from './RichEditorFooterActions'
import { TipTapToolbar } from './TipTapToolbar'

export {
  normalizeRichEditorHtmlForStorage,
  plainTextToRichEditorHtml,
  prepareRichEditorContent
}

function useLatestRef<T>(value: T) {
  const r = useRef(value)
  r.current = value
  return r
}

export interface RichEditorProps {
  /** Initial / external HTML (e.g. server-loaded body). */
  html?: string
  /** Called with serialized HTML whenever the document changes. */
  onChangeHtml?: (html?: string) => void
  readOnly?: boolean
  /** Restrict which block styles appear in the toolbar dropdown. Defaults to all. */
  blockStyles?: BlockStyle[]
}

const Shell = styled.div`
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  background: ${({ theme }) => theme.color.neutral.color11};
  display: flex;
  flex-direction: column;
  max-height: min(70vh, 720px);
  min-height: 200px;
  overflow: hidden;
  position: relative;
  resize: vertical;
`

const ContentScroller = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`

/** TipTap placeholder + prose typography; theme-driven */
const EditableWrap = styled.div`
  min-height: 160px;
  padding-bottom: 56px;
  position: relative;

  .tiptap {
    min-height: 180px;
    padding: 12px;
    outline: none;
    font-family: ${({ theme }) => theme.font.family.sansSerif};
  }

  .tiptap:focus-visible {
    outline: none;
  }

  .tiptap p.is-editor-empty:first-child::before {
    color: ${({ theme }) => theme.color.neutral.color6};
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }

  .rich-editor__prose p {
    margin: 0 0 0.5em;
  }

  .rich-editor__prose h1 {
    font-size: 1.75rem;
    margin: 0 0 0.5em;
  }

  .rich-editor__prose h2 {
    font-size: 1.4rem;
    margin: 0 0 0.5em;
  }

  .rich-editor__prose h3 {
    font-size: 1.2rem;
    margin: 0 0 0.5em;
  }

  .rich-editor__prose blockquote {
    margin: 0 0 0.5em;
    padding-left: 1em;
    border-left: 3px solid ${({ theme }) => theme.color.neutral.color7};
  }

  .rich-editor__prose ul,
  .rich-editor__prose ol {
    margin: 0 0 0.5em 1.25em;
    padding: 0;
  }

  .rich-editor__prose li {
    margin: 0.15em 0;
  }

  .rich-editor__prose a {
    color: ${({ theme }) => theme.color.status.primary};
    text-decoration: underline;
  }

  .rich-editor__prose strong {
    font-weight: ${({ theme }) => theme.font.weight.bold};
  }

  .rich-editor__prose em {
    font-style: italic;
  }

  .rich-editor__prose u {
    text-decoration: underline;
  }

  .rich-editor__prose s {
    text-decoration: line-through;
  }

  .rich-editor__prose sub,
  .rich-editor__prose sup {
    font-size: 0.75em;
  }

  .rich-editor__prose hr {
    margin: 1em 0;
    border: none;
    border-top: 1px solid ${({ theme }) => theme.color.neutral.color7};
  }
`

/**
 * TipTap-based rich text editor: styled-components + ui-kit theme tokens.
 */
export function RichEditor({
  html,
  onChangeHtml,
  readOnly = false,
  blockStyles
}: RichEditorProps) {
  const onChangeHtmlRef = useLatestRef(onChangeHtml)
  // Deferred until after TipTap's own initialization effects (and any onUpdate
  // they fire) have completed. Using a microtask so real user input is never dropped.
  const editorReadyRef = useRef(false)

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] }
      }),
      Subscript,
      Superscript,
      Underline,
      Link.configure({
        protocols: ['http', 'https', 'mailto', 'tel'],
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank'
        }
      }),
      Placeholder.configure({
        placeholder: 'Enter text…'
      })
    ],
    []
  )

  const editor = useEditor(
    {
      extensions,
      content: prepareRichEditorContent(html),
      editable: !readOnly,
      editorProps: {
        attributes: {
          class: 'rich-editor__prose',
          'aria-label': 'Rich text editor'
        }
      },
      onUpdate: ({ editor: ed }) => {
        if (!editorReadyRef.current) return
        const out = normalizeRichEditorHtmlForStorage(ed.getHTML())
        onChangeHtmlRef.current?.(out)
      }
    },
    []
  )

  // Arm after TipTap's own init effects (and any onUpdate they trigger) complete.
  // Promise.resolve() is a microtask — it runs before any user interaction but
  // after all synchronous initialization, so real edits are never silently dropped.
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!editor) return
    Promise.resolve().then(() => {
      editorReadyRef.current = true
    })
  }, [editor])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!editor) {
      return
    }
    editor.setEditable(!readOnly)
  }, [editor, readOnly])

  // Sync external `html` by diffing normalized strings against `getHTML()`. Do not
  // gate on last emitted onChange output — that can miss parent resets to older HTML.
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!editor) {
      return
    }
    const next = prepareRichEditorContent(html)
    const current = normalizeRichEditorHtmlForStorage(editor.getHTML())
    if (normalizeRichEditorHtmlForStorage(next) === current) {
      return
    }
    editor.commands.setContent(next, false)
  }, [editor, html])

  return (
    <Shell>
      {editor && !readOnly ? (
        <TipTapToolbar editor={editor} blockStyles={blockStyles} />
      ) : null}
      <ContentScroller>
        <EditableWrap>
          {editor ? <EditorContent editor={editor} /> : null}
        </EditableWrap>
      </ContentScroller>
      {editor && !readOnly ? <RichEditorFooterActions editor={editor} /> : null}
    </Shell>
  )
}

export default RichEditor
